import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, UserPlus, Check } from "lucide-react";
import { toast } from "sonner";

interface AssignUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: "curriculum" | "course";
  itemId: string;
  itemTitle: string;
  onAssign: (userId: string) => Promise<void>;
  assignedUserIds: string[];
}

export function AssignUserDialog({
  open,
  onOpenChange,
  type,
  itemId,
  itemTitle,
  onAssign,
  assignedUserIds,
}: AssignUserDialogProps) {
  const [search, setSearch] = useState("");
  const [assigningUserId, setAssigningUserId] = useState<string | null>(null);

  const { data: users, isLoading } = useQuery({
    queryKey: ["all_users_for_assignment"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, role")
        .order("full_name");

      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  const filteredUsers = users?.filter((user) =>
    user.full_name?.toLowerCase().includes(search.toLowerCase())
  );

  const handleAssign = async (userId: string) => {
    setAssigningUserId(userId);
    try {
      await onAssign(userId);
      toast.success(`User assigned to ${type}`);
    } catch (error: any) {
      toast.error(error.message || `Failed to assign user`);
    } finally {
      setAssigningUserId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Users</DialogTitle>
          <DialogDescription>
            Assign users to "{itemTitle}"
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <ScrollArea className="h-[300px] pr-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : filteredUsers && filteredUsers.length > 0 ? (
            <div className="space-y-2">
              {filteredUsers.map((user) => {
                const isAssigned = assignedUserIds.includes(user.id);
                const isAssigning = assigningUserId === user.id;

                return (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-2 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.avatar_url || undefined} />
                        <AvatarFallback>
                          {user.full_name?.charAt(0) || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{user.full_name}</p>
                        <Badge variant="outline" className="text-xs">
                          {user.role}
                        </Badge>
                      </div>
                    </div>

                    {isAssigned ? (
                      <Badge variant="secondary" className="gap-1">
                        <Check className="h-3 w-3" />
                        Assigned
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAssign(user.id)}
                        disabled={isAssigning}
                      >
                        {isAssigning ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <UserPlus className="h-4 w-4 mr-1" />
                            Assign
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              No users found
            </p>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
